import { useState, useEffect, useRef } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import API from '../../utils/api';
import {
  HiOutlineHome, HiOutlineUsers, HiOutlineLocationMarker,
  HiOutlineClipboardCheck, HiOutlineCurrencyDollar, HiOutlineCash,
  HiOutlineLogout, HiOutlineX, HiOutlineUserCircle, HiOutlineBell,
  HiOutlineChartBar,
} from 'react-icons/hi';

const Sidebar = ({ isOpen, onClose }) => {
  const { user, logout, updateUser } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const isAdmin = user?.role === 'admin';

  // Profile dropdown state
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const profileMenuRef = useRef(null);

  // Pending advance count for red badge
  const [pendingAdvanceCount, setPendingAdvanceCount] = useState(0);

  useEffect(() => {
    if (!isAdmin) return;
    const fetchPending = async () => {
      try {
        const { data } = await API.get('/admin/advances?status=pending');
        setPendingAdvanceCount(data.advances?.length || 0);
      } catch (e) { /* silent */ }
    };
    fetchPending();
    // Re-check every 30 seconds
    const iv = setInterval(fetchPending, 30000);
    return () => clearInterval(iv);
  }, [isAdmin]);

  // Close profile menu when clicking outside the entire user-card container.
  useEffect(() => {
    if (!profileMenuOpen) return;
    const onDown = (e) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(e.target)) {
        setProfileMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [profileMenuOpen]);

  const adminLinks = [
    { to: '/admin/dashboard',  icon: HiOutlineHome,             label: t('dashboard') },
    { to: '/admin/workers',    icon: HiOutlineUsers,            label: t('workers') },
    { to: '/admin/sites',      icon: HiOutlineLocationMarker,   label: t('sites') },
    { to: '/admin/attendance', icon: HiOutlineClipboardCheck,  label: t('attendance') },
    { to: '/admin/salary',     icon: HiOutlineCurrencyDollar,   label: t('salary') },
    { to: '/admin/advances',   icon: HiOutlineCash,             label: t('advances') },
    { to: '/admin/announcements', icon: HiOutlineBell,          label: 'ঘোষণা' },
    { to: '/admin/reports/monthly', icon: HiOutlineChartBar,   label: 'রিপোর্ট' },
  ];

  const workerLinks = [
    { to: '/worker/dashboard',  icon: HiOutlineHome,             label: t('dashboard') },
    { to: '/worker/attendance', icon: HiOutlineClipboardCheck,  label: t('attendance') },
    { to: '/worker/salary',     icon: HiOutlineCurrencyDollar,   label: t('salary') },
    { to: '/worker/advance',    icon: HiOutlineCash,             label: t('advances') },
  ];

  const links = isAdmin ? adminLinks : workerLinks;

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const goToProfile = () => {
    setProfileMenuOpen(false);
    onClose();
    navigate(isAdmin ? '/admin/profile' : '/worker/profile');
  };

  return (
    <>
      {/* Backdrop — only on mobile when open */}
      <div
        className={`fixed inset-0 bg-ink-900/60 backdrop-blur-sm z-40 lg:hidden transition-opacity duration-200 ${
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
        aria-hidden="true"
      />

      <aside
        className={`fixed top-0 left-0 z-50 h-full w-72 sidebar transform transition-transform duration-300 ease-out flex flex-col ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0 lg:static lg:z-auto`}
      >
        {/* Brand */}
        <div className="p-5 border-b border-ink-100 dark:border-ink-700">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <div className="w-9 h-9 rounded-xl bg-gradient-brand flex items-center justify-center text-white text-lg shadow-pop">
                  🔧
                </div>
                <h1 className="text-lg font-bold text-ink-900 dark:text-white">
                  প্লাম্বার সিস্টেম
                </h1>
              </div>
              <p className="text-xs text-ink-500 dark:text-ink-400 ml-11">
                {isAdmin ? 'অ্যাডমিন প্যানেল' : 'ওয়ার্কার প্যানেল'}
              </p>
            </div>
            <button
              onClick={onClose}
              className="lg:hidden p-2 text-ink-500 hover:bg-ink-100 dark:hover:bg-ink-700 rounded-lg"
              aria-label="Close menu"
            >
              <HiOutlineX className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto p-3 space-y-1">
          {links.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              onClick={onClose}
              data-welcome={link.to}
              className={({ isActive }) =>
                `group flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-150 ${
                  isActive
                    ? 'bg-brand-50 dark:bg-brand-900/30 text-brand-700 dark:text-brand-300 font-semibold shadow-sm'
                    : 'text-ink-600 dark:text-ink-300 hover:bg-ink-100 dark:hover:bg-ink-700/60'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <span className={`flex items-center justify-center w-9 h-9 rounded-lg transition-colors ${
                    isActive
                      ? 'bg-brand-600 text-white shadow-pop'
                      : 'bg-ink-100 dark:bg-ink-700 text-ink-500 dark:text-ink-400 group-hover:bg-white dark:group-hover:bg-ink-600'
                  }`}>
                    <link.icon className="w-5 h-5" />
                  </span>
                  <span className="text-sm">{link.label}</span>
                  {link.to === '/admin/advances' && pendingAdvanceCount > 0 && (
                    <span className="ml-auto flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-rose-600 text-white text-[10px] font-bold leading-none shadow-pop animate-fade-in">
                      {pendingAdvanceCount}
                    </span>
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        {/* User card + profile dropdown + logout — wrapper holds the ref
                    so dropdown clicks don't trigger the outside-click handler. */}
                <div ref={profileMenuRef} className="p-3 border-t border-ink-100 dark:border-ink-700 bg-ink-50/50 dark:bg-ink-900/30 relative">
                  <button
                    type="button"
                    onClick={() => setProfileMenuOpen((v) => !v)}
                    className="flex items-center gap-3 px-3 py-2.5 mb-1 w-full text-left rounded-xl hover:bg-white dark:hover:bg-ink-700/60 transition-colors"
                  >
                    <div className={
                      'w-10 h-10 rounded-xl flex items-center justify-center text-white font-semibold shadow-sm ' +
                      (isAdmin ? 'bg-gradient-to-br from-amber-500 to-amber-700' : 'bg-gradient-brand')
                    }>
                      {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-ink-900 dark:text-white truncate">
                        {user?.name}
                      </p>
                      <p className="text-xs text-ink-500 dark:text-ink-400 truncate">
                        {isAdmin ? '👑 অ্যাডমিন' : '👷 ওয়ার্কার'} · {user?.phone || user?.email || '—'}
                      </p>
                    </div>
                    <svg className={`w-4 h-4 text-ink-400 transition-transform ${profileMenuOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7" />
                    </svg>
                  </button>

                  {/* Profile dropdown */}
                  {profileMenuOpen && (
                    <div className="absolute bottom-full left-3 right-3 mb-1 bg-white dark:bg-ink-800 border border-ink-100 dark:border-ink-700 rounded-2xl shadow-pop overflow-hidden animate-fade-in">
                      <div className="p-3 border-b border-ink-100 dark:border-ink-700">
                        <p className="text-xs uppercase tracking-wider text-ink-500 mb-1">সাইন ইন করা আছে</p>
                        <p className="text-sm font-semibold text-ink-900 dark:text-white truncate">{user?.name}</p>
                        <p className="text-xs text-ink-500 dark:text-ink-400 truncate">{user?.email || user?.phone}</p>
                      </div>
                      <button
                type="button"
                onClick={goToProfile}
                className="w-full flex items-center gap-2 px-4 py-3 text-sm font-semibold text-ink-700 dark:text-ink-300 hover:bg-ink-50 dark:hover:bg-ink-900/30 transition-colors"
              >
                <HiOutlineUserCircle className="w-5 h-5 text-brand-600" />
                প্রোফাইল
              </button>
                      <button
                        type="button"
                        onClick={handleLogout}
                        className="w-full flex items-center gap-2 px-4 py-3 text-sm font-semibold text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-900/20 transition-colors border-t border-ink-100 dark:border-ink-700"
                      >
                        <HiOutlineLogout className="w-5 h-5" />
                        লগআউট
                      </button>
                    </div>
                  )}

                  <button
                    onClick={handleLogout}
                    data-welcome="logout"
                    className="flex items-center gap-3 w-full px-3 py-2.5 text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-900/20 rounded-xl transition-colors text-sm font-medium"
                  >
                    <span className="flex items-center justify-center w-9 h-9 rounded-lg bg-rose-50 dark:bg-rose-900/30">
                      <HiOutlineLogout className="w-5 h-5" />
                    </span>
                    <span>{t('logout')}</span>
                  </button>
                </div>
      </aside>
    </>
  );
};

export default Sidebar;